import { useEffect, useState, useCallback } from 'react';
import { SetMirrorData, MirrorData } from '@type/job-add';
import { SetFormValues } from '@components/common/Forms';
import { mutateProfileInList, SimpleEmployerProfileList } from '@components/templates/job-add/edit/functions/jobCompanyNameFunctions';

/** Props */
interface UseJobBenefits {
  jobTemplate?: ApiSchema<'JobModel2'>,
  setFormValues?: SetFormValues,
  apiEmployerProfileList?: SimpleEmployerProfileList[],
  reqMirrorData?: Pick<MirrorData, 'companyName'>,
  setMirrorData?: SetMirrorData,
  updateInputName?: string,
}

/**
 * @description Job benefits hook.
 * - `jobTemplate` - The job template.
 * - `setFormValues` - set form values function from useBjForm
 * - `apiEmployerProfileList` - The employer profile list.
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - The set mirror data function.
 * - `updateInputName` - The input name to be updated with setFormValues.
 */
export const useJobBenefits = (props: UseJobBenefits) => {
  // Destructure props
  const {
    jobTemplate,
    setFormValues,
    apiEmployerProfileList,
    reqMirrorData,
    setMirrorData,
    updateInputName = 'benefits',
  } = props;


  // STATES
  // **************************
  // Remove non profiles
  const [filteredProfiles, setFilteredProfiles] = useState<SimpleEmployerProfileList[] | undefined>(
    apiEmployerProfileList
      ?.filter((profile) => profile?.id !== 'from-search'
        && profile?.id !== 'search-employer-button'
        && profile?.id !== 'confidential'),
  );

  // Set selected benefits id's
  const [selectedBenefitsIds, setSelectedBenefitsIds] = useState<number[] | undefined>(jobTemplate?.benefits);

  // Keep track of selected profile name
  const [selectedProfileName, setSelectedProfileName] = useState<string | undefined>(
    jobTemplate?.companyName
    || filteredProfiles?.find((profile) => profile?.main === true)?.name,
  );


  // EFFECTS
  // **************************
  // Update selected profile name if another is selected in companyName field
  useEffect(() => {
    if (reqMirrorData?.companyName && reqMirrorData?.companyName !== selectedProfileName) {
      setSelectedProfileName(reqMirrorData?.companyName);
    }
  }, [reqMirrorData?.companyName, selectedProfileName]);

  // Update selected benefits if selected profile has benefits
  useEffect(() => {
    if (selectedProfileName) {
      const selectedProfile = filteredProfiles?.find((profile) => profile?.name === selectedProfileName);
      if (selectedProfile?.benefits) {
        setSelectedBenefitsIds(selectedProfile?.benefits);
        setMirrorData?.({ benefits: selectedProfile?.benefits }, false, true);
      }
    }
  }, [filteredProfiles, selectedProfileName, setMirrorData]);

  // Update the form values when the selected benefits id's change
  useEffect(() => {
    setFormValues?.({ [updateInputName]: selectedBenefitsIds });
  }, [selectedBenefitsIds, setFormValues, updateInputName]);


  // Functions
  // **************************
  // Mutate benefits for profile in list
  const updateCurrentProfileBenefits = useCallback((newBenefits?: number[]) => {
    if (newBenefits) {
      const updatedProfile = mutateProfileInList(filteredProfiles, selectedProfileName, { benefits: newBenefits });
      setFilteredProfiles(updatedProfile);
    }
  }, [filteredProfiles, selectedProfileName]);


  // Return data
  // **************************
  return {
    filteredProfiles,
    setFilteredProfiles,
    selectedBenefitsIds,
    setSelectedBenefitsIds,
    selectedProfileName,
    setSelectedProfileName,
    updateCurrentProfileBenefits,
  };
};
